import type { ChecklistItemMinimal } from '@/hooks/useStageScheduleChecklist';

export interface ChecklistItemProgress {
  totalLinked: number;
  completedCount: number;
  managementCheckedCount: number;
  subcontractorCheckedCount: number;
  requiredCount: number;
  completedRequiredCount: number;
  progressPct: number;
  allRequiredComplete: boolean;
  hasLinkedItems: boolean;
  missingManagement: number;
  missingSubcontractor: number;
  /** Total approval units required across all linked items */
  totalRequiredUnits: number;
  /** Total approval units completed across all linked items */
  totalCompletedUnits: number;
}

export interface LinkedChecklistItem {
  id: string;
  title: string;
  is_completed: boolean;
  management_checked: boolean;
  subcontractor_checked: boolean;
  category: string;
  checklistTitle: string | null;
  confidence: string | null;
  mappingType: string | null;
  mappingId: string | null;
  requires_management_check?: boolean;
  requires_subcontractor_check?: boolean;
}

/**
 * Check if a checklist item is considered "complete" based on its
 * management / subcontractor check requirements.
 */
export function isChecklistItemFullyComplete(
  item: ChecklistItemMinimal | LinkedChecklistItem
): boolean {
  const requiresMgmt =
    'requires_management_check' in item
      ? (item as Record<string, unknown>).requires_management_check !== false
      : true;
  const requiresSub =
    'requires_subcontractor_check' in item
      ? (item as Record<string, unknown>).requires_subcontractor_check === true
      : false;

  const mgmtOk = !requiresMgmt || item.management_checked;
  const subOk = !requiresSub || item.subcontractor_checked;

  return mgmtOk && subOk;
}

/**
 * Compute progress for a single schedule item from its linked checklist items.
 */
export function computeChecklistProgress(
  linkedItems: LinkedChecklistItem[]
): ChecklistItemProgress {
  if (linkedItems.length === 0) {
    return {
      totalLinked: 0,
      completedCount: 0,
      managementCheckedCount: 0,
      subcontractorCheckedCount: 0,
      requiredCount: 0,
      completedRequiredCount: 0,
      progressPct: 0,
      allRequiredComplete: true,
      hasLinkedItems: false,
      missingManagement: 0,
      missingSubcontractor: 0,
      totalRequiredUnits: 0,
      totalCompletedUnits: 0,
    };
  }

  let completedCount = 0;
  let mgmtChecked = 0;
  let subChecked = 0;
  let missingMgmt = 0;
  let missingSub = 0;
  let totalRequiredUnits = 0;
  let totalCompletedUnits = 0;

  for (const item of linkedItems) {
    const requiresMgmt =
      'requires_management_check' in item
        ? (item as Record<string, unknown>).requires_management_check !== false
        : true;
    const requiresSub =
      'requires_subcontractor_check' in item
        ? (item as Record<string, unknown>).requires_subcontractor_check === true
        : false;

    if (requiresMgmt) totalRequiredUnits += 1;
    if (requiresSub) totalRequiredUnits += 1;

    if (item.management_checked) {
      mgmtChecked++;
      if (requiresMgmt) totalCompletedUnits += 1;
    } else if (requiresMgmt) missingMgmt++;

    if (item.subcontractor_checked) {
      subChecked++;
      if (requiresSub) totalCompletedUnits += 1;
    } else if (requiresSub) missingSub++;

    const fullyComplete = isChecklistItemFullyComplete(item);
    if (fullyComplete) completedCount++;
  }

  const requiredCount = linkedItems.length;
  const progressPct =
    totalRequiredUnits > 0
      ? Math.round((totalCompletedUnits / totalRequiredUnits) * 100)
      : 0;

  return {
    totalLinked: linkedItems.length,
    completedCount,
    managementCheckedCount: mgmtChecked,
    subcontractorCheckedCount: subChecked,
    requiredCount,
    completedRequiredCount: completedCount,
    progressPct,
    allRequiredComplete: completedCount >= requiredCount,
    hasLinkedItems: true,
    missingManagement: missingMgmt,
    missingSubcontractor: missingSub,
    totalRequiredUnits,
    totalCompletedUnits,
  };
}

/**
 * Check if a schedule item is ready to be marked complete.
 * Rules:
 * 1. If linked checklists exist → all required checklist items must be complete
 * 2. If no linked checklists → allowed (but should show confirmation)
 */
export function isScheduleItemReadyToComplete(
  linkedItems: LinkedChecklistItem[]
): boolean {
  if (linkedItems.length === 0) return false; // no checklist = not "ready", but can be manually overridden
  return computeChecklistProgress(linkedItems).allRequiredComplete;
}

/**
 * Build a user-friendly block message when trying to complete a schedule item
 * whose checklist is not fully done.
 */
export function buildCompletionBlockMessage(
  linkedItems: LinkedChecklistItem[]
): string | null {
  const progress = computeChecklistProgress(linkedItems);
  if (!progress.hasLinkedItems) return null;
  if (progress.allRequiredComplete) return null;

  const parts: string[] = [];
  parts.push(`Required checklist approvals are not fully completed.`);
  parts.push(`Management complete: ${progress.managementCheckedCount} / ${progress.totalLinked}`);
  parts.push(`Subcontractor complete: ${progress.subcontractorCheckedCount} / ${progress.subcontractorCheckedCount + progress.missingSubcontractor}`);
  parts.push(`Approval progress: ${progress.totalCompletedUnits} / ${progress.totalRequiredUnits} units`);
  if (progress.missingManagement > 0) {
    parts.push(`${progress.missingManagement} management check(s) pending`);
  }
  if (progress.missingSubcontractor > 0) {
    parts.push(`${progress.missingSubcontractor} subcontractor check(s) pending`);
  }
  return parts.join('\n');
}

/**
 * Build a progress label string like "50% (4 / 8)" or "No checklist".
 */
export function buildProgressLabel(
  linkedItems: LinkedChecklistItem[]
): { pct: number; label: string; subLabel: string | null; blocked: boolean } {
  const progress = computeChecklistProgress(linkedItems);

  if (!progress.hasLinkedItems) {
    return { pct: 0, label: 'No checklist', subLabel: null, blocked: false };
  }

  const pct = progress.progressPct;
  const label = `${pct}%`;
  const subLabel = `${progress.completedRequiredCount} / ${progress.requiredCount} checklist`;
  const blocked = !progress.allRequiredComplete;

  return { pct, label, subLabel, blocked };
}

/**
 * Determine if a schedule item is considered "final complete".
 * THIS IS THE SINGLE SOURCE OF TRUTH for schedule completion.
 * Used by both schedule list rendering AND project progress aggregation.
 *
 * Returns true if ANY of the following are true:
 *   - scheduleItem.is_complete === true (DB flag, manual completion)
 *   - scheduleItem.progress >= 100
 *   - scheduleItem.actual_end_date != null (actual end recorded)
 *   - scheduleItem.completed_at != null (timestamp recorded)
 *
 * Returns true if all linked checklist items are fully approved.
 *
 * IMPORTANT: "Not mapped" does NOT affect completion.
 * Mapping status and completion status are SEPARATE concepts.
 * A manually completed item with no checklist mapping is COMPLETE.
 */
export function isScheduleItemFinalComplete(
  scheduleItem: { is_complete?: boolean; progress?: number; actual_end_date?: string | null; completed_at?: string | null },
  linkedItems: LinkedChecklistItem[]
): boolean {
  // DB manual completion takes priority
  if (scheduleItem.is_complete) return true;
  // Progress 100% also counts as complete
  if (scheduleItem.progress !== undefined && scheduleItem.progress >= 100) return true;
  // Actual end date recorded
  if (scheduleItem.actual_end_date != null && scheduleItem.actual_end_date !== '') return true;
  // Completed timestamp recorded
  if (scheduleItem.completed_at != null && scheduleItem.completed_at !== '') return true;

  // If linked checklist items exist and ALL are fully approved
  if (linkedItems.length > 0) {
    const allApproved = linkedItems.every((item) => isChecklistItemFullyComplete(item));
    if (allApproved) return true;
  }

  // No completion signal found — item is pending
  return false;
}

/**
 * Build a clear, user-facing reason string for why a schedule item
 * is or is not complete. Used in the UI to explain the state.
 */
export function getScheduleItemCompletionReason(
  scheduleItem: { is_complete?: boolean; progress?: number; actual_end_date?: string | null },
  linkedItems: LinkedChecklistItem[]
): { isComplete: boolean; reason: string } {
  const isComplete = isScheduleItemFinalComplete(scheduleItem, linkedItems);

  if (isComplete) {
    if (scheduleItem.is_complete) {
      return { isComplete: true, reason: 'Manually completed' };
    }
    if (scheduleItem.progress !== undefined && scheduleItem.progress >= 100) {
      return { isComplete: true, reason: 'Progress 100%' };
    }
    if (scheduleItem.actual_end_date != null && scheduleItem.actual_end_date !== '') {
      return { isComplete: true, reason: `Completed ${scheduleItem.actual_end_date}` };
    }
    if (linkedItems.length > 0) {
      return { isComplete: true, reason: 'All linked checklist items approved' };
    }
    return { isComplete: true, reason: 'Complete' };
  }

  // Not complete — explain why
  if (linkedItems.length === 0) {
    return { isComplete: false, reason: 'No checklist mapped — mark as complete manually or set progress to 100%' };
  }

  const missingMgmt = linkedItems.filter((i) => {
    const requiresMgmt = i.requires_management_check !== false;
    return requiresMgmt && !i.management_checked;
  }).length;
  const missingSub = linkedItems.filter((i) => {
    const requiresSub = i.requires_subcontractor_check === true;
    return requiresSub && !i.subcontractor_checked;
  }).length;

  const parts: string[] = [];
  if (missingMgmt > 0) parts.push(`${missingMgmt} management approval${missingMgmt > 1 ? 's' : ''} pending`);
  if (missingSub > 0) parts.push(`${missingSub} subcontractor approval${missingSub > 1 ? 's' : ''} pending`);
  if (parts.length === 0) parts.push('Checklist incomplete');

  return { isComplete: false, reason: parts.join(', ') };
}